export interface LLMConfig {
  provider: 'openai' | 'anthropic' | 'local';
  apiKey?: string;
  model?: string;
  baseURL?: string;
  maxTokens?: number;
  temperature?: number;
}

export interface LLMResponse {
  content: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  error?: string;
}

export interface DataContext {
  headers: string[];
  sampleData: any[];
  columnTypes: Record<string, string>;
  rowCount: number;
  fileName: string;
}

export abstract class LLMProvider {
  protected config: LLMConfig;

  constructor(config: LLMConfig) {
    this.config = config;
  }

  abstract generateResponse(prompt: string, context?: DataContext): Promise<LLMResponse>;
  abstract isAvailable(): boolean;
}

export class OpenAIProvider extends LLMProvider {
  async generateResponse(prompt: string, context?: DataContext): Promise<LLMResponse> {
    if (!this.config.apiKey) {
      return { content: '', error: 'OpenAI API key not configured' };
    }

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          model: this.config.model || 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: this.buildSystemPrompt(context)
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: this.config.maxTokens || 1000,
          temperature: this.config.temperature || 0.3
        })
      });

      if (!response.ok) {
        const error = await response.text();
        return { content: '', error: `OpenAI API error: ${error}` };
      }

      const data = await response.json();
      return {
        content: data.choices[0]?.message?.content || '',
        usage: {
          promptTokens: data.usage?.prompt_tokens || 0,
          completionTokens: data.usage?.completion_tokens || 0,
          totalTokens: data.usage?.total_tokens || 0
        }
      };
    } catch (error) {
      return { content: '', error: `Network error: ${error}` };
    }
  }

  isAvailable(): boolean {
    return !!this.config.apiKey;
  }

  private buildSystemPrompt(context?: DataContext): string {
    if (!context) {
      return 'You are a helpful assistant that answers questions about data.';
    }

    return `You are an expert data analyst helping users understand their dataset. 

Dataset Information:
- File: ${context.fileName}
- Rows: ${context.rowCount}
- Columns: ${context.headers.join(', ')}
- Column Types: ${Object.entries(context.columnTypes).map(([col, type]) => `${col} (${type})`).join(', ')}

Sample Data (first 3 rows):
${JSON.stringify(context.sampleData.slice(0, 3), null, 2)}

Instructions:
1. Provide clear, accurate answers based on the data
2. If you can't answer with the available data, say so clearly
3. Suggest specific visualizations when relevant
4. Use business-friendly language
5. Keep responses concise but informative
6. When suggesting charts, specify chart type, x-axis, y-axis, and any grouping`;
  }
}

export class AnthropicProvider extends LLMProvider {
  async generateResponse(prompt: string, context?: DataContext): Promise<LLMResponse> {
    if (!this.config.apiKey) {
      return { content: '', error: 'Anthropic API key not configured' };
    }

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.config.apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: this.config.model || 'claude-3-haiku-20240307',
          max_tokens: this.config.maxTokens || 1000,
          temperature: this.config.temperature || 0.3,
          system: this.buildSystemPrompt(context),
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ]
        })
      });

      if (!response.ok) {
        const error = await response.text();
        return { content: '', error: `Anthropic API error: ${error}` };
      }

      const data = await response.json();
      return {
        content: data.content[0]?.text || '',
        usage: {
          promptTokens: data.usage?.input_tokens || 0,
          completionTokens: data.usage?.output_tokens || 0,
          totalTokens: (data.usage?.input_tokens || 0) + (data.usage?.output_tokens || 0)
        }
      };
    } catch (error) {
      return { content: '', error: `Network error: ${error}` };
    }
  }

  isAvailable(): boolean {
    return !!this.config.apiKey;
  }

  private buildSystemPrompt(context?: DataContext): string {
    if (!context) {
      return 'You are a helpful assistant that answers questions about data.';
    }

    return `You are an expert data analyst helping users understand their dataset. 

Dataset Information:
- File: ${context.fileName}
- Rows: ${context.rowCount}
- Columns: ${context.headers.join(', ')}
- Column Types: ${Object.entries(context.columnTypes).map(([col, type]) => `${col} (${type})`).join(', ')}

Sample Data (first 3 rows):
${JSON.stringify(context.sampleData.slice(0, 3), null, 2)}

Instructions:
1. Provide clear, accurate answers based on the data
2. If you can't answer with the available data, say so clearly
3. Suggest specific visualizations when relevant
4. Use business-friendly language
5. Keep responses concise but informative
6. When suggesting charts, specify chart type, x-axis, y-axis, and any grouping`;
  }
}

export class LocalProvider extends LLMProvider {
  async generateResponse(prompt: string, context?: DataContext): Promise<LLMResponse> {
    // Placeholder for local LLM integration (Ollama, etc.)
    return { 
      content: 'Local LLM provider not yet implemented. Please configure OpenAI or Anthropic.',
      error: 'Local provider not implemented'
    };
  }

  isAvailable(): boolean {
    return false;
  }
}

export class LLMManager {
  private provider: LLMProvider | null = null;
  private config: LLMConfig | null = null;

  configure(config: LLMConfig) {
    this.config = config;
    
    switch (config.provider) {
      case 'openai':
        this.provider = new OpenAIProvider(config);
        break;
      case 'anthropic':
        this.provider = new AnthropicProvider(config);
        break;
      case 'local':
        this.provider = new LocalProvider(config);
        break;
      default:
        throw new Error(`Unsupported LLM provider: ${config.provider}`);
    }
  }

  async generateResponse(prompt: string, context?: DataContext): Promise<LLMResponse> {
    if (!this.provider) {
      return { content: '', error: 'LLM provider not configured' };
    }

    if (!this.provider.isAvailable()) {
      return { content: '', error: 'LLM provider is not available or not properly configured' };
    }

    return this.provider.generateResponse(prompt, context);
  }

  isConfigured(): boolean {
    return this.provider !== null && this.provider.isAvailable();
  }

  getConfig(): LLMConfig | null {
    return this.config;
  }
}

// Singleton instance
export const llmManager = new LLMManager();

// Auto-configure from environment variables
export const initializeLLMFromEnv = () => {
  // Frontend-side model calls are intentionally disabled.
  // Q&A in this app must be served via backend/API orchestration.
  console.log('ℹ️ Frontend LLM providers disabled. Use backend API for AI features.');
};
